/* eslint-disable jsx-a11y/click-events-have-key-events */
import {useCallback, useLayoutEffect, useRef, useState} from 'preact/hooks';
import {createPortal} from 'preact/compat';
import {useEffect} from 'react';

import {
  useListener,
  SwipeDirection,
  useSwipe,
  SwipeResult,
  useShadowRootOnBody,
} from 'utilities/dom';
import {useTabFocusTrap} from 'utilities/focus-trap';
import {animateElement} from 'utilities/animations';
import {useI18n} from 'utilities/i18n';

import styles from './MobileDrawer.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children?: any;
}

const TRANSITION_DURATION = 500;
const SWIPE_THRESHOLD = 30;

export const MobileDrawer = ({isOpen, onClose, children}: ModalProps) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [focusRef, setFocusTrap] = useTabFocusTrap();
  const [internalIsOpen, setInternalIsOpen] = useState(isOpen);
  const initialBodyState = useRef({
    overflow: document.body.style.overflow,
    position: document.body.style.position,
    top: document.body.style.top,
    scrollPosition: window.pageYOffset || document.documentElement.scrollTop,
  });
  const swipeAreaRef = useRef<HTMLDivElement | null>(null);
  const shadowRootRef = useShadowRootOnBody();
  const {translate: t} = useI18n();

  const showDrawer = internalIsOpen && shadowRootRef.current;

  const swipeEndCallback = useCallback(
    (swipe: SwipeResult) => {
      const {direction, distance} = swipe;
      const element = focusRef.current;

      if (direction === SwipeDirection.Down && distance > SWIPE_THRESHOLD) {
        animateElement('down', element, distance);
        onClose();
      } else if (element) {
        element.style.transform = '';
      }
    },
    [onClose, focusRef],
  );

  const swipeMoveCallback = useCallback(
    (swipe: SwipeResult) => {
      const {direction, distance} = swipe;
      const element = focusRef.current;
      if (element) {
        if (direction === SwipeDirection.Down) {
          element.style.transform = `translateY(${distance}px)`;
        } else if (direction === SwipeDirection.Up) {
          element.style.transform = '';
        }
      }
    },
    [focusRef],
  );

  useSwipe(swipeAreaRef, swipeEndCallback, swipeMoveCallback);

  // Close drawer on Esc key press
  useListener(
    'keyup',
    (event: KeyboardEvent) => {
      if (['Escape', 'Esc'].includes(event.key)) {
        animateElement('down', focusRef.current);
        onClose();
      }
    },
    [onClose],
  );

  const resetBodyStyles = useCallback(() => {
    const {position, overflow, scrollPosition, top} = initialBodyState.current;
    document.body.style.overflow = overflow;
    document.body.style.position = position;
    document.body.style.top = top;
    window.scrollTo(0, scrollPosition);
  }, []);

  useEffect(() => {
    if (showDrawer && focusRef.current) {
      focusRef.current.focus();
    }
  }, [showDrawer, focusRef]);

  useLayoutEffect(() => {
    // Reset on dismount
    return () => {
      resetBodyStyles();
    };
  }, [resetBodyStyles]);

  useLayoutEffect(() => {
    if (isOpen) {
      initialBodyState.current = {
        ...initialBodyState.current,
        scrollPosition:
          window.pageYOffset || document.documentElement.scrollTop,
      };

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${initialBodyState.current.scrollPosition}px`;
      setInternalIsOpen(isOpen);
    } else {
      resetBodyStyles();
      // Give time for the animation to complete before removing the element
      // Remove 50 from TRANSITION_DURATION to avoid flickering of the element
      setTimeout(() => {
        setInternalIsOpen(isOpen);
      }, TRANSITION_DURATION - 50);
    }
  }, [focusRef, isOpen, resetBodyStyles]);

  useLayoutEffect(() => {
    if (internalIsOpen) {
      animateElement('up', focusRef.current);
    }
  }, [focusRef, internalIsOpen]);

  const onOverlayClick = useCallback(
    (event: Event) => {
      if (event.target === event.currentTarget) {
        animateElement('down', focusRef.current);
        onClose();
      }
    },
    [focusRef, onClose],
  );

  const onCloseKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (['Enter', ' '].includes(event.key)) {
        animateElement('down', focusRef.current);
        onClose();
      }
    },
    [focusRef, onClose],
  );

  const visibility = isOpen ? 'visible' : 'hidden';

  return showDrawer
    ? createPortal(
        <div
          className={[styles.overlay, styles[visibility]].join(' ')}
          onClick={onOverlayClick}
          ref={overlayRef}
        >
          <div
            ref={setFocusTrap}
            className={[styles.drawer, styles[visibility]].join(' ')}
            tabIndex={-1}
          >
            <div
              className={styles.tab}
              role="button"
              tabIndex={0}
              aria-label={t('popup.close', {title: 'Shop Promise'})}
              onKeyUp={onCloseKeyUp}
            />
            <div ref={swipeAreaRef} className={styles.swipeArea} />
            {children}
          </div>
        </div>,
        shadowRootRef.current as any,
      )
    : null;
};
